<template>
    <v-col v-bind="DASHBOARD_WIDGET_COLUMNS.LARGE">
        <v-card>
            <v-toolbar color="grey-lighten-4" density="compact" flat>
                <template #title>{{ widget.body?.title }}</template>
            </v-toolbar>
            <v-divider />
            <v-card-text>
                <v-data-table :headers="headers" :items="rows" density="compact" hide-default-footer item-value="name" />
            </v-card-text>
        </v-card>
    </v-col>
</template>

<script setup lang="ts">
/*
Organization / ordering applied:
1. Imports
2. Props / Emits
3. Constants
4. Stores & top-level services
5. Reactive state (refs/reactive)
6. Validation rules & setup
7. Computed
8. Handlers / methods
9. Helpers / lifecycle
*/

// 1. Imports
import { computed } from 'vue'

import type { DashboardPermission, DashboardWidget } from '@/stores/dashboard'
import { DASHBOARD_WIDGET_COLUMNS } from '@/views/Dashboard/dashboardUtils'

// 2. Props / Emits
const props = withDefaults(
    defineProps<{
        widget: DashboardWidget
        permissions?: DashboardPermission[]
    }>(),
    { permissions: () => [] }
)

type TotalReportRow = { period?: string; name?: string; currency?: string; totalPrice?: unknown }

// 7. Computed
const uniquePeriodHeaders = computed(() => {
    const content = props.widget?.body?.content
    const rows: TotalReportRow[] = Array.isArray(content) ? (content as TotalReportRow[]) : []
    const periods = rows.map((item) => item.period).filter((p): p is string => p != null)
    return periods.filter((item, index) => periods.indexOf(item) === index)
})

const headers = computed(() => [
    { title: 'Nazwa', key: 'name', sortable: false },
    { title: 'Waluta', key: 'currency', sortable: false },
    ...uniquePeriodHeaders.value.map((period, index) => ({ title: period, key: `period${index + 1}`, sortable: false }))
])

const rows = computed(() => {
    const byName = new Map<string, Record<string, unknown>>()

    const content = props.widget?.body?.content
    const list: TotalReportRow[] = Array.isArray(content) ? (content as TotalReportRow[]) : []
    list.forEach((item) => {
        const name = item.name ?? ''
        if (!byName.has(name)) {
            byName.set(name, { name: item.name, currency: item.currency })
        }

        const row = byName.get(name)!
        const periodIndex = uniquePeriodHeaders.value.indexOf(item.period ?? '')
        row[`period${periodIndex + 1}`] = item.totalPrice
    })

    return Array.from(byName.values()).map((row) => {
        uniquePeriodHeaders.value.forEach((_, index) => {
            if (!row[`period${index + 1}`]) row[`period${index + 1}`] = '-'
        })
        return row
    })
})
</script>
